import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, AlertTriangle, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { VALUE_TYPES, PART_STATUS } from "./inventoryConstants";

const emptyPart = {
  name: "", part_number: "", alternate_part_numbers: [], oem_part_number: "",
  vendor_part_number: "", manufacturer: "", brand: "", description: "",
  category: "", subcategory: "", component_group: "",
  compat_tractor: false, compat_chassis: false, compat_yard_equipment: false, engine_compatibility: "",
  quantity_on_hand: 0, quantity_reserved: 0, reorder_point: "",
  value_type: "estimated", unit_cost: 0, value_source: "",
  location_id: "", notes: "", status: "in_stock", photo: null, default_vendor_id: "",
};

const Section = ({ title, children }) => (
  <div className="border-t border-slate-100 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0">
    <h3 className="text-sm font-semibold text-slate-800 mb-3">{title}</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="text-xs text-slate-500 mb-1 block">{label}</Label>
    {children}
  </div>
);

export const PartFormModal = ({ open, onClose, part, locations, categories, vendors, onSaved, currentUser }) => {
  const [form, setForm] = useState(emptyPart);
  const [altInput, setAltInput] = useState("");
  const [duplicates, setDuplicates] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!part?.id;
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  useEffect(() => {
    if (open) {
      setForm(part ? { ...emptyPart, ...part, reorder_point: part.reorder_point ?? "" } : emptyPart);
      setDuplicates([]);
      setAltInput("");
    }
  }, [open, part]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runDuplicateCheck = useCallback(async () => {
    if (!form.part_number?.trim()) return;
    try {
      const res = await inventoryApi.checkDuplicate({
        part_number: form.part_number,
        alternate_part_numbers: form.alternate_part_numbers,
        exclude_id: part?.id,
      });
      setDuplicates(res.matches || []);
    } catch {
      setDuplicates([]);
    }
  }, [form.part_number, form.alternate_part_numbers, part?.id]);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => set("photo", ev.target.result);
    reader.readAsDataURL(file);
  };

  const addAlternate = () => {
    const v = altInput.trim();
    if (v && !form.alternate_part_numbers.includes(v)) {
      set("alternate_part_numbers", [...form.alternate_part_numbers, v]);
    }
    setAltInput("");
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.part_number.trim()) {
      toast.error("Part Name and Part Number are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      quantity_on_hand: Number(form.quantity_on_hand) || 0,
      quantity_reserved: Number(form.quantity_reserved) || 0,
      reorder_point: form.reorder_point === "" ? null : Number(form.reorder_point),
      unit_cost: Number(form.unit_cost) || 0,
      location_id: form.location_id || null,
      default_vendor_id: form.default_vendor_id || null,
    };
    try {
      if (isEdit) {
        await inventoryApi.updatePart(part.id, { ...payload, updated_by: userName });
        toast.success("Part updated");
      } else {
        await inventoryApi.createPart({ ...payload, created_by: userName });
        toast.success("Part created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save part");
    }
    setSaving(false);
  };

  const selectedCategory = categories.find((c) => c.name === form.category);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col" hideClose data-testid="part-form-modal">
        <DialogTitle className="sr-only">{isEdit ? "Edit Part" : "Add New Part"}</DialogTitle>
        <DialogDescription className="sr-only">Inventory part record form</DialogDescription>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">{isEdit ? "Edit Part" : "Add New Part"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="part-form-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {duplicates.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="duplicate-warning">
              <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-1">
                <AlertTriangle className="w-4 h-4" /> Possible duplicate part(s) found
              </div>
              <ul className="text-xs text-amber-700 space-y-0.5">
                {duplicates.map((d) => (
                  <li key={d.id}>• {d.part_number} — {d.name} {d.manufacturer ? `(${d.manufacturer})` : ""} · On hand: {d.quantity_on_hand}</li>
                ))}
              </ul>
            </div>
          )}

          <Section title="Identification">
            <Field label="Part Name *">
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="part-name-input" />
            </Field>
            <Field label="Part Number *">
              <Input value={form.part_number} onChange={(e) => set("part_number", e.target.value)} onBlur={runDuplicateCheck} data-testid="part-number-input" />
            </Field>
            <Field label="Alternate Part Numbers" full>
              <div className="flex gap-2">
                <Input value={altInput} onChange={(e) => setAltInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAlternate())} placeholder="Type and press Enter" />
                <Button type="button" variant="outline" onClick={addAlternate}>Add</Button>
              </div>
              {form.alternate_part_numbers.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.alternate_part_numbers.map((a) => (
                    <span key={a} className="inline-flex items-center gap-1 text-xs bg-slate-100 px-2 py-1 rounded">
                      {a}
                      <button onClick={() => set("alternate_part_numbers", form.alternate_part_numbers.filter((x) => x !== a))}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </Field>
            <Field label="OEM Part Number"><Input value={form.oem_part_number || ""} onChange={(e) => set("oem_part_number", e.target.value)} /></Field>
            <Field label="Vendor Part Number"><Input value={form.vendor_part_number || ""} onChange={(e) => set("vendor_part_number", e.target.value)} /></Field>
            <Field label="Manufacturer"><Input value={form.manufacturer || ""} onChange={(e) => set("manufacturer", e.target.value)} /></Field>
            <Field label="Brand"><Input value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} /></Field>
            <Field label="Description" full><Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></Field>
          </Section>

          <Section title="Classification">
            <Field label="Category">
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="part-category-select">
                <option value="">Select...</option>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Subcategory">
              {selectedCategory?.subcategories?.length > 0 ? (
                <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)}>
                  <option value="">Select...</option>
                  {selectedCategory.subcategories.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <Input value={form.subcategory || ""} onChange={(e) => set("subcategory", e.target.value)} placeholder="Optional" />
              )}
            </Field>
            <Field label="Component Group" full><Input value={form.component_group || ""} onChange={(e) => set("component_group", e.target.value)} /></Field>
          </Section>

          <Section title="Compatibility">
            <div className="sm:col-span-2 flex flex-wrap gap-4">
              {[["compat_tractor", "Tractor"], ["compat_chassis", "Chassis"], ["compat_yard_equipment", "Yard Equipment"]].map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} className="w-4 h-4" data-testid={`compat-${k}`} />
                  {lbl}
                </label>
              ))}
            </div>
            <Field label="Engine Compatibility" full><Input value={form.engine_compatibility || ""} onChange={(e) => set("engine_compatibility", e.target.value)} placeholder="e.g. Cummins X15, DD15" /></Field>
          </Section>

          <Section title="Inventory & Costing">
            <Field label="Quantity On Hand"><Input type="number" value={form.quantity_on_hand} onChange={(e) => set("quantity_on_hand", e.target.value)} disabled={isEdit} data-testid="part-qty-input" />{isEdit && <span className="text-[10px] text-slate-400">Adjust via ledger</span>}</Field>
            <Field label="Reorder Point"><Input type="number" value={form.reorder_point} onChange={(e) => set("reorder_point", e.target.value)} placeholder="Low-stock threshold" /></Field>
            <Field label="Unit Cost ($)"><Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} data-testid="part-cost-input" /></Field>
            <Field label="Value Type">
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.value_type} onChange={(e) => set("value_type", e.target.value)}>
                {VALUE_TYPES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Value Source" full><Input value={form.value_source || ""} onChange={(e) => set("value_source", e.target.value)} placeholder="e.g. Vendor invoice #1234" /></Field>
          </Section>

          <Section title="Location & Status">
            <Field label="Location">
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.location_id || ""} onChange={(e) => set("location_id", e.target.value)} data-testid="part-location-select">
                <option value="">Unassigned</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.label || l.container || l.id}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.status} onChange={(e) => set("status", e.target.value)}>
                {Object.entries(PART_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Default Vendor">
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.default_vendor_id || ""} onChange={(e) => set("default_vendor_id", e.target.value)}>
                <option value="">None</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </Field>
            <Field label="Notes" full><Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
            <Field label="Part Photo" full>
              <div className="flex items-center gap-3">
                {form.photo ? (
                  <div className="relative">
                    <img src={form.photo} alt="part" className="w-16 h-16 rounded-lg object-cover border" />
                    <button onClick={() => set("photo", null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 text-sm text-blue-600 cursor-pointer">
                    <Upload className="w-4 h-4" /> Upload photo
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                  </label>
                )}
              </div>
            </Field>
          </Section>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-slate-200 flex-shrink-0">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" onClick={handleSubmit} disabled={saving} data-testid="part-form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? "Save Changes" : "Create Part"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
